import { roundedRect } from '@bn-games/render-helpers';
import type { Tile } from '../game/types.js';
import {
  DIM_ALPHA,
  HIGHLIGHT_RING_WIDTH,
  SELECTED_RING_LIFT,
  SELECTED_RING_WIDTH,
  TILE_BACK_INNER_RADIUS,
  TILE_BACK_INSET,
  TILE_BASE_HEIGHT,
  TILE_BORDER_WIDTH,
  TILE_CORNER_RADIUS,
  TILE_HOVER_GLOW_BLUR,
  TILE_SHADOW_BLUR,
  TILE_SHADOW_COLOR,
  TILE_SHADOW_OFFSET_Y,
} from '../utils/constants.js';
import { drawHonorGlyph, drawSuitContent, type TileBounds } from './TileGlyphs.js';
import type { RenderPalette } from './palette.js';

/** Quarter-turn orientations for opponent hands rendered edge-on/upside-down. */
export type TileRotation = 0 | 90 | 180 | 270;

/**
 * Where to render a tile. Mahjong tiles are always portrait — `width`/`height`
 * describe the tile in its own upright frame regardless of `rotation`; the
 * renderer rotates about the rect's centre so callers place edge-on opponent
 * tiles by rotation alone, never by pre-swapping the dimensions.
 */
export interface TileRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly rotation?: TileRotation;
}

export interface DrawTileOptions {
  /** Soft glow around the tile (hovered). */
  readonly hover?: boolean;
  /** Outline ring on the currently-selected hand tile. */
  readonly selected?: boolean;
  /** Fade a disabled / ghost tile. */
  readonly dim?: boolean;
  /** Ring marking a tile a call (Pung/Chi/Ron) can claim. */
  readonly highlight?: boolean;
}

function tileScale(rect: TileRect): number {
  return rect.height / TILE_BASE_HEIGHT;
}

/**
 * Sets up alpha + centre-rotation, then hands `paint` a local `TileBounds`
 * whose origin is already inside the rotated frame (top-left at −w/2,−h/2).
 */
function drawWithTransform(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  dim: boolean | undefined,
  paint: (b: TileBounds) => void,
): void {
  ctx.save();
  if (dim) ctx.globalAlpha = DIM_ALPHA;
  ctx.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
  const rotation = rect.rotation ?? 0;
  if (rotation !== 0) ctx.rotate((rotation * Math.PI) / 180);
  paint({
    x: -rect.width / 2,
    y: -rect.height / 2,
    width: rect.width,
    height: rect.height,
    scale: tileScale(rect),
  });
  ctx.restore();
}

export function drawTileFace(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  tile: Tile,
  palette: RenderPalette,
  options: DrawTileOptions = {},
): void {
  drawWithTransform(ctx, rect, options.dim, (b) => {
    const cornerRadius = TILE_CORNER_RADIUS * b.scale;

    if (options.hover) {
      ctx.shadowColor = palette.hoverGlow;
      ctx.shadowBlur = TILE_HOVER_GLOW_BLUR;
    }

    // Drop shadow under the tile.
    ctx.save();
    ctx.shadowColor = TILE_SHADOW_COLOR;
    ctx.shadowBlur = TILE_SHADOW_BLUR;
    ctx.shadowOffsetY = TILE_SHADOW_OFFSET_Y;
    roundedRect(ctx, b.x, b.y, b.width, b.height, cornerRadius);
    ctx.fillStyle = palette.tileFaceBase;
    ctx.fill();
    ctx.restore();

    // Glossy top-to-bottom gradient (carries the hover glow if set).
    const grad = ctx.createLinearGradient(b.x, b.y, b.x, b.y + b.height);
    grad.addColorStop(0, palette.tileFaceHighlight);
    grad.addColorStop(0.6, palette.tileFaceBase);
    grad.addColorStop(1, palette.tileFaceShadow);
    ctx.fillStyle = grad;
    roundedRect(ctx, b.x, b.y, b.width, b.height, cornerRadius);
    ctx.fill();

    // Crisp border + content (no shadow bleed).
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.strokeStyle = palette.tileFaceBorder;
    ctx.lineWidth = TILE_BORDER_WIDTH;
    roundedRect(ctx, b.x + 0.5, b.y + 0.5, b.width - 1, b.height - 1, cornerRadius);
    ctx.stroke();

    if (tile.kind === 'suit') drawSuitContent(ctx, b, tile.suit, tile.rank, palette);
    else drawHonorGlyph(ctx, b, tile.honor, palette);

    if (options.highlight) {
      ctx.strokeStyle = palette.claimGlow;
      ctx.lineWidth = HIGHLIGHT_RING_WIDTH * b.scale;
      roundedRect(ctx, b.x + 1, b.y + 1, b.width - 2, b.height - 2, cornerRadius);
      ctx.stroke();
    }

    if (options.selected) {
      const lift = SELECTED_RING_LIFT;
      ctx.strokeStyle = palette.selectedRing;
      ctx.lineWidth = SELECTED_RING_WIDTH;
      roundedRect(
        ctx,
        b.x - lift,
        b.y - lift,
        b.width + lift * 2,
        b.height + lift * 2,
        cornerRadius + lift,
      );
      ctx.stroke();
    }
  });
}

/** Face-down tile for opponents' concealed hands + the wall. */
export function drawTileBack(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  palette: RenderPalette,
  options: DrawTileOptions = {},
): void {
  drawWithTransform(ctx, rect, options.dim, (b) => {
    const cornerRadius = TILE_CORNER_RADIUS * b.scale;

    ctx.save();
    ctx.shadowColor = TILE_SHADOW_COLOR;
    ctx.shadowBlur = TILE_SHADOW_BLUR;
    ctx.shadowOffsetY = TILE_SHADOW_OFFSET_Y;
    roundedRect(ctx, b.x, b.y, b.width, b.height, cornerRadius);
    ctx.fillStyle = palette.tileBackBase;
    ctx.fill();
    ctx.restore();

    const grad = ctx.createLinearGradient(b.x, b.y, b.x, b.y + b.height);
    grad.addColorStop(0, palette.tileBackPattern);
    grad.addColorStop(0.5, palette.tileBackBase);
    grad.addColorStop(1, palette.tileBackPattern);
    ctx.fillStyle = grad;
    roundedRect(ctx, b.x, b.y, b.width, b.height, cornerRadius);
    ctx.fill();

    // Faux-inlay inset panel.
    const inset = TILE_BACK_INSET * b.scale;
    ctx.strokeStyle = palette.tileBackPattern;
    ctx.lineWidth = TILE_BORDER_WIDTH;
    roundedRect(
      ctx,
      b.x + inset,
      b.y + inset,
      b.width - inset * 2,
      b.height - inset * 2,
      TILE_BACK_INNER_RADIUS * b.scale,
    );
    ctx.stroke();

    ctx.strokeStyle = palette.tileBackBorder;
    ctx.lineWidth = TILE_BORDER_WIDTH;
    roundedRect(ctx, b.x + 0.5, b.y + 0.5, b.width - 1, b.height - 1, cornerRadius);
    ctx.stroke();
  });
}
